/**
 * kumo.mail — プレイヤーが「ゲームからのお知らせメール」を受け取る同意（2026-09-27 Creator mail-consent）。
 * サーバーは apps/server/src/modules/mailconsent（config の `features.mail` を ON にしたゲームだけ。既定 OFF）。
 *
 *   // 設定画面などに「お知らせをメールで受け取る」のチェック欄（初期はチェックなし）を置き、本人がチェックしたときだけ呼ぶ
 *   const r = await kumo.mail.subscribe({ email, consentVersion: 'news-2026-09' });  // → 確認メールが届く（r.state === 'pending'）
 *   const s = await kumo.mail.status();        // { state: 'none' | 'pending' | 'confirmed' | 'withdrawn', email, … }
 *   await kumo.mail.unsubscribe();             // すぐに止まる
 *
 *   // メールアドレスの登録・ログインの画面なら、登録と同じ呼び出しに付けるだけ（チェックボックスは初期はチェックなし・チェックされたときだけ渡す）
 *   await kumo.auth.signUpWithEmail(email, password, name, checkbox.checked ? { mailOptIn: { consentVersion: 'signup-2026-09' } } : {});
 *
 * 送るのはクリエイター（ダッシュボード・CLI・MCP）で、ここは「受け取ってよい」の記録だけ。確認のしかた（サーバーが決める）:
 *   - 申し込みのアドレスが本人の**確認済みの**メール（確認済みのメールのログイン・Google 等が確認済みと言ったメール）と同じ → その場で confirmed
 *   - 登録したばかりの**確認前の**メールと同じ → awaiting_verification（登録の確認メールのリンクが押されたら confirmed。確認メールは 1 通だけ）
 *   - それ以外のアドレス → 確認メール（ダブルオプトイン）。本人がリンクを押すまで confirmed にならない（押すまでは送られない）
 *
 * 最初の公開で閉じている（2026-09-29 creator/mail-close・Kumo 管理の決定。一覧と WHY は launchHidden.ts の 'mail'）:
 *   init() の返す kumo.mail は unsubscribe だけ（subscribe / status は外す）。kumo.auth の mailOptIn は型から外し、渡されても無視する（下の applyMailOptIn）。
 *   中身はこのまま残す = launchHidden.ts の一覧から 'mail' を外せば元どおり（サーバーも KUMO_UNLOCKED_FEATURES=mail で開く）
 */

import type { KumoCore } from './core.js';
import { KumoError } from './core.js';
import { isHiddenNow, type HiddenFeature } from './launchHidden.js';

export interface MailSubscribeOptions {
  /**
   * 送り先のアドレス。省くとメールのログインのアドレスを使う（無ければ 400 email_required）。
   * ゲスト（メールのログインが無い人）でも、アドレスを渡せば申し込める（確認メールで受信箱の持ち主を確かめる）
   */
  email?: string;
  /**
   * 同意の画面の文言の版（短い名前・64 文字まで。例 'news-2026-09' / 'v3'）。**必須**。
   * WHY: サーバーは「いつ・どの文言に同意したか」を記録に残す（メールの法律で同意の記録を求められたときに示すため）。
   * チェック欄の文言を変えたら版も変える（古い版で同意した人の記録は古い版のまま残る）
   */
  consentVersion: string;
  /**
   * 同意の取り方（記録に残る）。既定 'sdk_checkbox' = ゲームの画面のチェック欄。自前のサーバーから API を直接呼ぶなら 'api'。
   * （登録・ログインの画面のチェックボックスは 'signup_checkbox'。kumo.auth の mailOptIn が自動で付ける）
   */
  method?: 'sdk_checkbox' | 'signup_checkbox' | 'api';
}

/**
 * 登録・ログインの呼び出しに付ける「お知らせを受け取る」（kumo.auth.signUpWithEmail / linkEmail / signInWithProvider / linkProvider の options）。
 * **チェックボックスがチェックされたときだけ渡す**（初期はチェックなし。渡さなければ SDK は何も呼ばない = 遊ぶことと抱き合わせない）
 */
export interface MailOptIn {
  /** チェックボックスの文言の版（短い名前・64 文字まで。例 'signup-2026-09'）。文言を変えたら版も変える */
  consentVersion: string;
}

/** SDK の中で読む形（auth.ts / authProviders.ts）。公開の型に mailOptIn が無い間も、中では同じ名前で読む */
export interface MailOptInInternal {
  mailOptIn?: MailOptIn;
}

/**
 * kumo.auth の options に足す mailOptIn（公開の型）。'mail' を隠している間は空 = 型の補完に出ない（TypeScript では渡すと型エラー）。
 * WHY 型で外すか（2026-09-29 creator/mail-close）: kumo.mail.subscribe / status を型から外したのと同じ（launchHidden.ts の WithoutHidden）。
 *   補完に出ると「登録の画面でゲームのお知らせを申し込める」と読めるが、サーバーは申し込みを閉じている（403 feature_unavailable）。
 *   launchHidden.ts から 'mail' を外せば { mailOptIn?: MailOptIn } に戻る（型の式なので書き換え不要）
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- 隠している間は「足すものが無い」を表す空の型
export type MailOptInField = 'mail' extends HiddenFeature ? {} : MailOptInInternal;

/** options から mailOptIn を読む（公開の型に無い間も、JavaScript から渡された値をここで拾って applyMailOptIn に渡す = そこで無視する） */
export function mailOptInOf(opts: object | undefined): MailOptIn | undefined {
  return (opts as MailOptInInternal | undefined)?.mailOptIn;
}

export interface MailSubscribeResult {
  /**
   * pending = 確認メールを送った（本人がリンクを押すと confirmed）/ confirmed = 確認済みのアドレス（何も送っていない）/
   * awaiting_verification = 登録したばかりのメールと同じ（登録の確認メールのリンクが押されたら confirmed。別の確認メールは送っていない）
   */
  state: 'pending' | 'confirmed' | 'awaiting_verification';
  email: string;
  /** false = 直前（60 秒以内）に送ったので送らなかった・確認済みだった */
  sent: boolean;
  /** 確認のリンクの期限（pending のとき） */
  expiresAt?: string;
}

export interface MailStatus {
  /** none = 申し込んだことがない / pending = 確認メール待ち / awaiting_verification = 登録のメール確認待ち / confirmed = 受け取る / withdrawn = 止めた */
  state: 'none' | 'pending' | 'awaiting_verification' | 'confirmed' | 'withdrawn';
  email: string | null;
  consentVersion: string | null;
  confirmedAt: string | null;
  withdrawnAt: string | null;
  /** 確認済みのまま別のアドレスを申し込み中なら、その申し込み（確認されるまでは前のアドレスに届く） */
  pending: { email: string; expiresAt: string | null } | null;
}

/**
 * 使うときの決まり（ゲームの作者へ。サーバーでも一部を強制している）:
 *   - **チェック欄は初期状態でチェックなし**にする。最初からチェックされた欄・黙っての申し込みは同意にならない（GDPR・日本の特定電子メール法）
 *   - **遊ぶことと抱き合わせない**: 申し込まないと遊べない・報酬がもらえない、にしない（GDPR Art 7(4)）。サーバーも同意の有無で他の API を変えない
 *   - 申し込めるのは**大人のプレイヤーだけ**（13 歳未満・10 代は 403 age_restricted。kids のゲームでは機能自体を ON にできない）。
 *     age_restricted のときはチェック欄を出さない / 隠す
 *   - 止める（unsubscribe）は申し込みと同じくらい見つけやすい場所に置く。機能を OFF にした後でも止められる
 * 失敗: 403 feature_disabled（config の features.mail が OFF）・403 age_restricted・400 email_required・503 mail_unavailable（少し後で再試行）
 */
export function createMail(core: KumoCore) {
  return {
    /** 申し込み → 確認メール（ダブルオプトイン）。本人がチェック欄をチェックしたときだけ呼ぶ */
    subscribe: (opts: MailSubscribeOptions) =>
      core.request<MailSubscribeResult>('POST', '/v1/mail/subscribe', {
        ...(opts.email !== undefined ? { email: opts.email } : {}),
        consentVersion: opts.consentVersion,
        ...(opts.method ? { method: opts.method } : {})
      }),
    /** 今の状態（チェック欄の初期表示に使う: confirmed / pending ならチェック済みで表示してよい） */
    status: () => core.request<MailStatus>('GET', '/v1/mail/subscription'),
    /** 止める（即時）。申し込んでいなくても成功する */
    unsubscribe: () => core.request<{ state: 'withdrawn'; withdrawn: boolean }>('POST', '/v1/mail/unsubscribe')
  };
}

export type KumoMail = ReturnType<typeof createMail>;

/**
 * 登録・ログインが成功した後に「お知らせを受け取る」を申し込む（kumo.auth の mailOptIn。auth.ts / authProviders.ts が呼ぶ）。
 * - optIn が無ければ何もしない（チェックされていない = 何も記録しない）
 * - **失敗してもログインは成功のまま**（例外を投げない）: ログインとお知らせは別の話。age_restricted（大人でない）・feature_disabled
 *   （features.mail が OFF）などは console.warn に出すだけ。状態は kumo.mail.status() で確かめられる
 * - email: メールの登録・連携は入力したアドレスを渡す。外部ログインは省く（SDK はプロバイダのメールを知らない → サーバーが
 *   その人の確認済みのメールを使う）
 */
export async function applyMailOptIn(core: KumoCore, optIn: MailOptIn | undefined, email?: string): Promise<MailSubscribeResult | null> {
  if (!optIn) return null;
  // 最初の公開で 'mail' を閉じている間（2026-09-29 creator/mail-close・Kumo 管理の決定）は、渡されても黙って無視する（往復も警告も無し）。
  // WHY 断らず（例外を投げず）無視するか: 上の決まり「失敗してもログインは成功のまま」と同じ考え方。ログインとお知らせは別の話で、
  //   閉じている間に mailOptIn を渡す古いコード・Skill（JavaScript = 型のエラーが出ない）があっても、ログインは今までどおり通したい。
  // WHY 呼ばずに止めるか（呼んで 403 を警告にしないか）: サーバーは申し込みを閉じている（403 feature_unavailable）= 呼んでも必ず断られる往復に
  //   なるだけで、プレイヤーのコンソールに「お知らせの申し込みに失敗」と出す理由も無い（index.ts の課金の回収を隠している間に呼ばないのと同じ）。
  //   本人が後で申し込みたくなっても、開け直した後に kumo.mail.subscribe で申し込める（黙っての申し込みにはしない）
  if (isHiddenNow('mail')) return null;
  try {
    return await core.request<MailSubscribeResult>('POST', '/v1/mail/subscribe', {
      ...(email !== undefined ? { email } : {}),
      consentVersion: optIn.consentVersion,
      method: 'signup_checkbox'
    });
  } catch (e) {
    console.warn('[game-backend] email updates opt-in failed', e instanceof KumoError ? e.code : e);
    return null;
  }
}
